/**
 * NewWeddingWizard: orchestrates the 4-step guided flow (PRD §14.3).
 * Deliberately a wizard, not a single long form — each step is one
 * decision at a time, matching the "feels guided, never a blank canvas"
 * design philosophy from the very first planning conversation.
 *
 * Three starting paths (as of ceremony milestone):
 *   - Blank:     empty room, coordinator builds everything manually
 *   - Reception: one of four opinionated generation styles
 *   - Ceremony:  generated chair rows + optional arbor; all objects editable
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import { useCreateWedding } from '../../api/weddings';
import { useCreateLayout } from '../../api/layouts';
import { useRoomDefinitions } from '../../api/roomDefinitions';
import { useObjectCatalog, useInventoryLimits } from '../../api/inventoryLimits';
import { generateLayout } from '../../domain/generation/generateLayout';
import { generateCeremonyLayout } from '../../domain/generation/generateCeremonyLayout';
import type { HeadTableType, LayoutStyle, Room } from '../../domain/types';
import { StepVenueSelect } from './StepVenueSelect';
import { StepGuestCount } from './StepGuestCount';
import { StepStyleSelect } from './StepStyleSelect';
import { StepConfirmGenerate } from './StepConfirmGenerate';

export interface WizardData {
  coupleNames: string;
  eventDate: string;
  room: Room | null;
  guestCount: number;
  headTableType: HeadTableType;
  headTablePartySize: number | null;
  layoutStyle: LayoutStyle | null;
  /** True if the coordinator chose "Start Blank" instead of a generation
   * style. When true, the first layout created for this wedding has zero
   * objects rather than running the generation engine. */
  startBlank: boolean;
  /** True if the coordinator chose the Ceremony path. When true, the
   * ceremony generation engine runs instead of the reception engine. */
  startCeremony: boolean;
  /** Number of chairs to generate for the ceremony layout.
   * Defaults to guestCount so the coordinator doesn't have to re-enter it,
   * but they can adjust independently (some ceremonies seat fewer than the
   * full guest count). Only meaningful when startCeremony is true. */
  ceremonyChairCount: number;
  /** Whether to include an arbor in the generated ceremony layout. */
  ceremonyIncludeArbor: boolean;
}

const INITIAL_DATA: WizardData = {
  coupleNames: '',
  eventDate: '',
  room: null,
  guestCount: 100,
  headTableType: 'head_table',
  headTablePartySize: 6,
  layoutStyle: null,
  startBlank: false,
  startCeremony: false,
  ceremonyChairCount: 100,
  ceremonyIncludeArbor: true,
};

interface NewWeddingWizardProps {
  onClose: () => void;
}

export function NewWeddingWizard({ onClose }: NewWeddingWizardProps) {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const createWedding = useCreateWedding();
  const createLayout = useCreateLayout();
  const { data: roomDefinitions } = useRoomDefinitions();
  const { data: catalog } = useObjectCatalog();
  const { data: inventoryLimits } = useInventoryLimits();
  const [createError, setCreateError] = useState<string | null>(null);

  const [step, setStep] = useState(1);
  const [data, setData] = useState<WizardData>(INITIAL_DATA);

  function update(changes: Partial<WizardData>) {
    setData((prev) => ({ ...prev, ...changes }));
  }

  function goNext() {
    setStep((s) => Math.min(4, s + 1));
  }

  function goBack() {
    if (step === 1) {
      onClose();
    } else {
      setStep((s) => s - 1);
    }
  }

  async function handleCreate() {
    if (!profile || !data.room) return;

    // All three paths need a valid room. Beyond that:
    // - Blank: no further checks
    // - Reception: needs a layoutStyle
    // - Ceremony: ceremonyChairCount > 0 (guaranteed by the form)
    const needsLayoutStyle = !data.startBlank && !data.startCeremony;
    if (needsLayoutStyle && !data.layoutStyle) return;

    setCreateError(null);
    try {
      const wedding = await createWedding.mutateAsync({
        coupleNames: data.coupleNames || 'Untitled Wedding',
        eventDate: data.eventDate || new Date().toISOString().slice(0, 10),
        room: data.room,
        guestCount: data.guestCount,
        headTableType: data.headTableType,
        headTablePartySize: data.headTableType === 'head_table' ? data.headTablePartySize : null,
        // Ceremony and blank layouts still need a non-null layoutStyle on the
        // record (the column isn't nullable). classic_banquet is a harmless
        // neutral default — no generation runs for these paths so the value
        // is never used to drive furniture placement.
        layoutStyle:
          data.startBlank || data.startCeremony
            ? 'classic_banquet'
            : data.layoutStyle!,
        createdBy: profile.id,
      });

      const roomDefinition = roomDefinitions?.find((r) => r.room === data.room);

      if (data.startBlank) {
        // --- BLANK PATH ---
        await createLayout.mutateAsync({
          weddingId: wedding.id,
          objects: [],
          generatedAt: new Date().toISOString(),
        });
      } else if (data.startCeremony) {
        // --- CEREMONY PATH ---
        // Generate ceremony objects if we have the catalog. If the catalog
        // isn't loaded (rare edge case), fall back to a blank layout so the
        // coordinator isn't left without a layout record.
        const objects =
          roomDefinition && catalog
            ? generateCeremonyLayout({
                chairCount: data.ceremonyChairCount,
                includeArbor: data.ceremonyIncludeArbor,
                roomDefinition,
                objectCatalog: catalog,
              })
            : [];
        await createLayout.mutateAsync({
          weddingId: wedding.id,
          objects,
          generatedAt: new Date().toISOString(),
        });
      } else {
        // --- RECEPTION PATH ---
        if (roomDefinition && catalog && inventoryLimits) {
          const result = generateLayout({
            room: wedding.room,
            guestCount: wedding.guestCount,
            headTableType: wedding.headTableType,
            headTablePartySize: wedding.headTablePartySize,
            layoutStyle: wedding.layoutStyle,
            roomDefinition,
            objectCatalog: catalog,
            inventoryLimits,
          });
          await createLayout.mutateAsync({
            weddingId: wedding.id,
            objects: result.objects,
            generatedAt: new Date().toISOString(),
          });
        }
        // If room/catalog/inventory data somehow isn't loaded yet, the
        // wedding is still created successfully — the editor's own
        // "no layout yet" fallback lets the coordinator generate or start
        // blank from there instead of losing the wedding record entirely.
      }

      navigate(`/weddings/${wedding.id}`);
    } catch {
      setCreateError('Something went wrong creating this wedding. Please check your connection and try again.');
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-700/40 px-4">
      <div className="w-full max-w-2xl rounded-xl bg-ivory-50 shadow-elevated">
        <div className="flex items-center justify-between border-b border-ivory-200 px-6 py-3">
          <button onClick={goBack} className="font-body text-sm text-ink-400 hover:text-ink-600">
            {'\u2190'} Back
          </button>
          <p className="font-body text-xs uppercase tracking-wide text-ink-400">Step {step} of 4</p>
        </div>

        <div className="px-6 py-8">
          {step === 1 && (
            <StepVenueSelect
              data={data}
              onChange={update}
              onNext={goNext}
            />
          )}
          {step === 2 && (
            <StepGuestCount data={data} onChange={update} onNext={goNext} />
          )}
          {step === 3 && (
            <StepStyleSelect data={data} onChange={update} onNext={goNext} />
          )}
          {step === 4 && (
            <StepConfirmGenerate
              data={data}
              onChangeCoupleNames={(coupleNames) => update({ coupleNames })}
              onChangeEventDate={(eventDate) => update({ eventDate })}
              onConfirm={handleCreate}
              submitting={createWedding.isPending}
              error={createError}
            />
          )}
        </div>
      </div>
    </div>
  );
}
